import React from "react";
import { createRoot } from "react-dom/client"; // Update import
import { ChakraProvider } from "@chakra-ui/react";
import App from "./App";
import './pages/medstudent/clinic.css';
import './css/faxInbox.css';
import reportWebVitals from "./components/reportWebVitals";
import { TimerProvider } from "./components/TimerContext";
import { basename, resetTime, loadConfig, getBackendURL } from "./components/env";
import { BrowserRouter as Router } from "react-router-dom";
import {
  VOSK_JS_URL,
  fetchVoskManifest,
  voskModelUrl,
} from "./services/vosk/config.js";
import { installFetchTelemetry } from "./utils/fetchTelemetry";
import { recordDataOutEvent } from "./services/rlsAudit";

// Browser downloads bypass Django middleware. Capture every download link at
// the document boundary so notes, reports, audio, patient bundles and future
// download buttons all produce a server-written DATA_OUT event.
if (!window.__phiDownloadAuditInstalled) {
  window.__phiDownloadAuditInstalled = true;
  document.addEventListener("click", (event) => {
    const link = event.target?.closest?.("a[download]");
    if (!link) return;
    const filename = `${link.getAttribute("download") || ""} ${link.getAttribute("href") || ""}`;
    const action = link.dataset.auditAction || (/\.(csv|tsv|xlsx|zip)(?:\?|\s|$)/i.test(filename) ? "EXPORT" : "DOWNLOAD");
    const name = filename.toLowerCase();
    const inferred = name.includes("transcript") || name.includes("recording") || name.includes("audio")
      ? ["notes", "notes_c"]
      : name.includes("lab") ? ["labs", "patient_labs"]
        : name.includes("med") ? ["medications", "patient_medications"]
          : name.includes("fax") ? ["fax", "faxes"]
            : name.includes("report") ? ["report", "patient_reports"]
              : name.includes("form") ? ["form", "forms"]
                : name.includes("appointment") || name.includes("schedule") ? ["appointments", "appointments"]
                  : name.includes("patient") ? ["patient", "patients"] : ["document", "documents"];
    const recordCount = Number.parseInt(link.dataset.auditRecordCount || "0", 10);
    void recordDataOutEvent(action, {
      resource_type: link.dataset.auditResourceType || inferred[0],
      resource_table: link.dataset.auditResourceTable || inferred[1],
      resource_id: link.dataset.auditResourceId || null,
      patient_id: link.dataset.patientId || link.dataset.auditPatientId || null,
      record_count: Number.isFinite(recordCount) && recordCount > 0 ? recordCount : undefined,
    });
  }, true);
}

const shouldEnableVerboseLogs = () => {
  try {
    return window.localStorage?.getItem("debug:app") === "true";
  } catch {
    return true;
  }
};

// DEBUG: Always enable logs for now
const ENABLE_ALL_LOGS = true;

const shouldSuppressWarn = (message) =>
  message.includes("__REACT_DEVTOOLS_GLOBAL_HOOK__") ||
  message.includes("quill Overwriting modules/cursors") ||
  message.includes("quill:toolbar ignoring attaching to nonexistent format");

// Always enable logs - remove suppression for debugging
// if (!shouldEnableVerboseLogs()) {
if (!ENABLE_ALL_LOGS) {
  // Suppress noisy debug logs by default. Keep errors intact.
  console.log = () => {};

  const originalWarn = console.warn;
  console.warn = (...args) => {
    const message = args.filter((arg) => typeof arg === "string").join(" ");
    if (shouldSuppressWarn(message)) {
      return;
    }
    originalWarn(...args);
  };
}

const container = document.getElementById("root");
const root = createRoot(container); // Create root

const prefetchClientSttAssets = () => {
  const basePath =
    typeof basename === "string" && basename.trim() ? basename.trim() : "";
  const withBase = (asset) => {
    const normalized = asset.startsWith("/") ? asset : `/${asset}`;
    return basePath ? `${basePath}${normalized}` : normalized;
  };
  // Legacy same-origin candidates. The notesai-vosk-service CDN URLs
  // (resolved from the manifest) are prepended once the manifest loads.
  const sameOriginCandidates = [
    withBase("/vendors/vosk.js"),
    withBase("/vosk-model-small-en-us-0.15-flat.tar.gz"),
    withBase("/vosk-model-small-en-us-0.15.tar.gz"),
  ];

  const prefetch = (uniqueAssets) => {
    if (!uniqueAssets || uniqueAssets.length === 0) return;
    for (const href of uniqueAssets) {
      try {
        const link = document.createElement("link");
        link.rel = "prefetch";
        link.as = "fetch";
        link.href = href;
        link.crossOrigin = "anonymous";
        document.head.appendChild(link);
      } catch (_) {}
    }
    const triggerFetch = () => {
      uniqueAssets.forEach((href) => {
        fetch(href, { cache: "force-cache" }).catch(() => {});
      });
    };
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(triggerFetch);
    } else {
      setTimeout(triggerFetch, 1200);
    }
  };

  // Always prefetch same-origin fallbacks so the loader still works
  // if the manifest is unreachable.
  prefetch(Array.from(new Set(sameOriginCandidates)));

  // Then asynchronously resolve the manifest and prefetch the CDN URLs.
  (async () => {
    try {
      const manifest = await fetchVoskManifest();
      const modelUrl = manifest?.models?.[0] ? voskModelUrl(manifest.models[0]) : "";
      const cdnUrls = [VOSK_JS_URL, modelUrl].filter(Boolean);
      prefetch(Array.from(new Set(cdnUrls)));
    } catch (_) {
      // Manifest unreachable; same-origin prefetch already in flight.
    }
  })();
};

const init = async () => {
  try {
    // Load configuration from backend before rendering
    await loadConfig();

    // Live Activity: stamp backend calls with the tab's session id + current page.
    installFetchTelemetry({ backendUrl: getBackendURL() });

    prefetchClientSttAssets();

    root.render(
      <TimerProvider resetTime={resetTime}>
        <Router
          basename={basename}
          future={{
            //Meant to remove react warning messages (removable)
            v7_startTransition: true, //Meant to remove react warning messages (removable)
            v7_relativeSplatPath: true, //Meant to remove react warning messages (removable)
          }} //Meant to remove react warning messages (removable)
        >
          <App />
        </Router>
      </TimerProvider>,
    );

    reportWebVitals();
  } catch (error) {
    // Show user-friendly error message if config fails to load
    console.error("Failed to initialize app:", error);
    root.render(
      <div
        style={{
          padding: "40px",
          fontFamily: "Arial, sans-serif",
          textAlign: "center",
          maxWidth: "600px",
          margin: "50px auto",
        }}
      >
        <h1 style={{ color: "#e53e3e", marginBottom: "20px" }}>
          Configuration Error
        </h1>
        <p style={{ color: "#4a5568", marginBottom: "10px" }}>
          Failed to load application configuration from the backend.
        </p>
        <p style={{ color: "#718096", fontSize: "14px", marginBottom: "20px" }}>
          {error.message}
        </p>
        <p style={{ color: "#4a5568", fontSize: "14px" }}>Please ensure:</p>
        <ul
          style={{
            textAlign: "left",
            color: "#718096",
            fontSize: "14px",
            display: "inline-block",
          }}
        >
          <li>The backend server is running</li>
          <li>Environment variables SUPABASE_URL and SUPABASE_KEY are set</li>
          <li>Check the browser console for more details</li>
        </ul>
      </div>,
    );
  }
};

init();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
